import React from 'react'
import type { usePerioStreamingVoice } from '@hooks/usePerioStreamingVoice'
import {
  useVoiceTranscriptSnapshot,
  type VoiceTranscriptSource,
} from '@hooks/useVoiceTranscriptFeed'
import ChartingVoiceStatus, {
  VoicePromptGroup,
} from '@components/pages/Charting/ChartingVoiceStatus.component'
import { getProbeSiteLabels } from '@utils/perio.utils'

interface PerioVoiceStatusProps {
  voice: Pick<
    ReturnType<typeof usePerioStreamingVoice>,
    'phase' | 'keyword' | 'backlog' | 'message'
  > &
    VoiceTranscriptSource
}

/** High-signal phrases shown as chips while charting. */
export const PERIO_VOICE_QUICK_PROMPTS: string[] = [
  '3 2 1',
  'Jump to number 1',
  'Bleeding 9 mesial',
  'Furcation on mesial',
  'Next',
  'Undo',
  'Pause',
  'Resume',
]

/** Full reference for the expandable “More commands” list. */
export const PERIO_VOICE_PROMPT_GROUPS: VoicePromptGroup[] = [
  {
    label: 'Readings',
    prompts: ['3 2 1', '2 3 3, 2 2 2', 'Mobility one two zero'],
  },
  {
    label: 'Navigation',
    prompts: [
      'Next',
      'Back',
      'Jump to number 1',
      'Select number 1',
      'Jump to tooth 8 lingual',
      'Jump to gingival margin',
      'Jump to clinical attachment level',
    ],
  },
  {
    label: 'Markers',
    prompts: [
      'Bleeding 9 mesial',
      '9 bleeding mesial',
      'Bleeding 9 buccal',
      'Suppuration on distal',
      'Furcation on mesial',
      'Clear furcation on mesial',
      'Calculus on all sites',
      'Mobility 26 grade 1',
      'Bleeding 19 buccal repeat 5',
      'Suppuration interproximal quadrant 2',
    ],
  },
  {
    label: 'Controls',
    prompts: [
      'Pause',
      'Resume',
      'Undo',
      'Clear pending',
      'Reset',
      'Clear all',
      'Stop',
    ],
  },
]

const PerioVoiceStatus = ({
  voice,
}: PerioVoiceStatusProps): React.ReactElement => {
  const transcript = useVoiceTranscriptSnapshot(voice)
  const active = ['connecting', 'listening', 'processing'].includes(voice.phase)
  const paused = active && voice.keyword.paused
  const midContent =
    voice.keyword.pending.length > 0 ? (
      <div className="perio-voice-pending" role="status">
        Pending:{' '}
        {getProbeSiteLabels(voice.keyword.cursor.tooth)
          .map(
            (site, index) => `${site}: ${voice.keyword.pending[index] ?? '—'}`,
          )
          .join(' · ')}
        <br />
        Waiting for {3 - voice.keyword.pending.length} more.
      </div>
    ) : undefined

  return (
    <ChartingVoiceStatus
      phase={voice.phase}
      backlog={voice.backlog}
      message={voice.message}
      history={transcript.history}
      liveText={transcript.liveText}
      queued={transcript.queued}
      paused={paused}
      ended={voice.keyword.ended}
      ariaLabel="Voice transcription"
      midContent={midContent}
      quickPrompts={PERIO_VOICE_QUICK_PROMPTS}
      promptGroups={PERIO_VOICE_PROMPT_GROUPS}
      tip="Readings are D · B · M for teeth 1–8 and 25–32, M · B · D for 9–24. Pause briefly between phrases."
    />
  )
}

export default PerioVoiceStatus
